import { useEffect, useMemo, useState } from "react";
import ERPAnalyticsHeader from "../components/ERPAnalyticsHeader";
import { api } from "../lib/api";
import {
  Workflow,
  Search,
  CheckCircle2,
  AlertTriangle,
  Layers3,
  Database,
  X,
  Users,
  GitBranch,
  Calculator,
} from "lucide-react";

const VALIDATION_LABELS = {
  unmapped: "Unmapped",
  technically_mapped: "Technically Mapped",
  business_validation_pending: "Business Validation Pending",
  approved: "Approved",
};

export default function ERPFlowPlanner() {
  const [loading, setLoading] = useState(false);
  const [payload, setPayload] = useState({ summary: {}, records: [] });
  const [search, setSearch] = useState("");
  const [module, setModule] = useState("");
  const [validation, setValidation] = useState("");
  const [selected, setSelected] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/erp-flow-planner", {
        params: {
          search: search || undefined,
          module: module || undefined,
          validation_status: validation || undefined,
        },
      });
      setPayload(r.data || { summary: {}, records: [] });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const summary = payload.summary || {};
  const records = payload.records || [];

  const modules = useMemo(() => {
    return Object.keys(summary.modules || {}).sort((a, b) =>
      String(a).localeCompare(String(b))
    );
  }, [summary.modules]);

  return (
    <div className="erp-home-shell">
      <ERPAnalyticsHeader />

      <main className="erp-content">
        <div className="erp-page-title-row">
          <div>
            <h1>ERP Flow & Analytics Planner</h1>
            <p>
              Connect ERP screens, SQL tables, business questions, formulas,
              roles, validation, and future analytics widgets before building
              CEO and module dashboards.
            </p>
          </div>

          <button className="erp-primary-btn" onClick={load} disabled={loading}>
            <Workflow size={15} strokeWidth={1.8} />
            {loading ? "Refreshing..." : "Refresh Planner"}
          </button>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-4 gap-3 mb-4">
          <Stat
            label="Planner Records"
            value={summary.total || 0}
            icon={Layers3}
          />
          <Stat
            label="Active"
            value={summary.active || 0}
            icon={CheckCircle2}
          />
          <Stat
            label="Technically Mapped"
            value={summary.validation?.technically_mapped || 0}
            icon={Database}
          />
          <Stat
            label="Approved"
            value={summary.validation?.approved || 0}
            icon={CheckCircle2}
          />
        </div>

        <div className="erp-card mb-4">
          <div className="database-filter-row">
            <div className="database-search">
              <Search size={15} strokeWidth={1.8} />
              <input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search module, screen, table, question, widget..."
              />
            </div>

            <select value={module} onChange={(e) => setModule(e.target.value)}>
              <option value="">All modules</option>
              {modules.map((m) => (
                <option key={m} value={m}>
                  {m} ({summary.modules?.[m] || 0})
                </option>
              ))}
            </select>

            <select
              value={validation}
              onChange={(e) => setValidation(e.target.value)}
            >
              <option value="">All validation</option>
              {Object.entries(VALIDATION_LABELS).map(([key, label]) => (
                <option key={key} value={key}>
                  {label}
                </option>
              ))}
            </select>

            <button className="erp-action-btn small-action" onClick={load}>
              Apply Filters
            </button>
          </div>
        </div>

        <div className="grid grid-cols-1 xl:grid-cols-12 gap-4">
          <div className="xl:col-span-8 erp-card">
            <div className="erp-panel-head">
              <div>
                <h2>Business Mapping Registry</h2>
                <p>
                  {records.length} mapped business items. Select any row to
                  inspect formulas, source tables, lineage, roles, and widget
                  planning.
                </p>
              </div>
            </div>

            <div className="database-table-wrap">
              <table className="tbl database-table">
                <thead>
                  <tr>
                    <th>Module</th>
                    <th>Screen / Area</th>
                    <th>Business Question</th>
                    <th>Source Tables</th>
                    <th>Widget Plan</th>
                    <th>Validation</th>
                  </tr>
                </thead>

                <tbody>
                  {records.map((r) => (
                    <tr
                      key={r.id}
                      className="database-clickable-row"
                      onClick={() => setSelected(r)}
                      title="Click to inspect ERP business mapping"
                    >
                      <td>
                        <strong>{r.module}</strong>
                        <div className="database-muted">
                          {r.submodule || "—"}
                        </div>
                      </td>

                      <td>
                        <strong>{r.screen_name || "—"}</strong>
                        <div className="database-muted">
                          {r.menu_path || r.business_process || "—"}
                        </div>
                      </td>

                      <td className="database-small-list">
                        {(r.business_questions || [])[0] || "—"}
                      </td>

                      <td className="database-small-list">
                        {(r.source_tables || []).slice(0, 3).join(", ") || "—"}
                      </td>

                      <td>
                        <strong>{r.widget_title || "Not planned"}</strong>
                        <div className="database-muted">
                          {r.widget_type || "—"}
                        </div>
                      </td>

                      <td>
                        <ValidationBadge value={r.validation_status} />
                      </td>
                    </tr>
                  ))}

                  {records.length === 0 && (
                    <tr>
                      <td
                        colSpan={6}
                        className="text-center py-6"
                        style={{ color: "var(--c-text-muted)" }}
                      >
                        No ERP Flow Planner records found.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>

          <div className="xl:col-span-4 erp-card">
            <div className="erp-panel-head">
              <div>
                <h2>Planning Detail</h2>
                <p>
                  Business meaning first, technical mapping second, dashboard
                  output last.
                </p>
              </div>
              <GitBranch size={18} strokeWidth={1.8} />
            </div>

            {!selected && (
              <div className="database-empty-detail">
                Select a planner record to inspect the complete ERP-to-analytics
                mapping.
              </div>
            )}

            {selected && (
              <div className="database-detail">
                <div className="database-detail-head">
                  <div>
                    <h3>{selected.widget_title || selected.screen_name}</h3>
                    <p>{selected.module}</p>
                  </div>

                  <button
                    className="btn btn-ghost"
                    onClick={() => setSelected(null)}
                  >
                    <X size={14} strokeWidth={1.8} />
                  </button>
                </div>

                <div className="database-detail-grid">
                  <Info label="Module" value={selected.module} />
                  <Info label="Submodule" value={selected.submodule || "—"} />
                  <Info
                    label="Priority"
                    value={String(selected.priority || "medium").toUpperCase()}
                  />
                  <Info
                    label="Validation"
                    value={
                      VALIDATION_LABELS[selected.validation_status] ||
                      selected.validation_status ||
                      "—"
                    }
                  />
                </div>

                <Section
                  title="Business Purpose"
                  icon={Workflow}
                  text={selected.business_purpose}
                />

                <ListSection
                  title="Business Questions"
                  items={selected.business_questions}
                />

                <ListSection
                  title="Source Tables"
                  items={selected.source_tables}
                />

                <Section
                  title="Formula"
                  icon={Calculator}
                  text={selected.formula}
                />

                <Section
                  title="Formula Notes"
                  icon={AlertTriangle}
                  text={selected.formula_notes}
                />

                <ObjectListSection
                  title="Join Logic"
                  items={selected.join_keys}
                />

                <ObjectListSection
                  title="Filters"
                  items={selected.filters}
                />

                <ListSection title="Roles" icon={Users} items={selected.roles} />

                <ObjectListSection
                  title="Data Lineage"
                  items={selected.data_lineage}
                />

                <Section
                  title="Validation Notes"
                  icon={CheckCircle2}
                  text={selected.validation_notes}
                />
              </div>
            )}
          </div>
        </div>
      </main>
    </div>
  );
}

function Stat({ label, value, icon: Icon }) {
  return (
    <div className="erp-stat-card">
      <div>
        <span>{label}</span>
        <strong>{value}</strong>
        <small>ERP planning</small>
      </div>

      <div className="erp-stat-icon">
        <Icon size={19} strokeWidth={1.8} />
      </div>
    </div>
  );
}

function Info({ label, value }) {
  return (
    <div className="database-info">
      <span>{label}</span>
      <strong>{value}</strong>
    </div>
  );
}

function ValidationBadge({ value }) {
  const label = VALIDATION_LABELS[value] || value || "Unmapped";

  return (
    <span
      className={`database-status ${
        value === "approved"
          ? "active"
          : value === "technically_mapped"
            ? "active"
            : "skipped"
      }`}
    >
      {label}
    </span>
  );
}

function Section({ title, text, icon: Icon }) {
  if (!text) return null;

  return (
    <div className="database-field-group">
      <h4>
        {Icon && <Icon size={13} strokeWidth={1.8} />}
        {title}
      </h4>
      <p>{text}</p>
    </div>
  );
}

function ListSection({ title, items, icon: Icon }) {
  const list = items || [];
  if (!list.length) return null;

  return (
    <div className="database-field-group">
      <h4>
        {Icon && <Icon size={13} strokeWidth={1.8} />}
        {title}
      </h4>
      <div>
        {list.map((item, index) => (
          <span key={`${title}-${index}`}>{String(item)}</span>
        ))}
      </div>
    </div>
  );
}

function ObjectListSection({ title, items }) {
  const list = items || [];
  if (!list.length) return null;

  return (
    <div className="database-columns-box">
      <h4>{title}</h4>

      {list.map((item, index) => (
        <div key={`${title}-${index}`} className="database-column-row">
          <strong>{item.description || item.purpose || item.field || `Item ${index + 1}`}</strong>
          <span>
            {item.left_table && item.right_table
              ? `${item.left_table}.${item.left_field} → ${item.right_table}.${item.right_field}`
              : item.source || ""}
          </span>
          <small>{item.required === true ? "Required" : ""}</small>
        </div>
      ))}
    </div>
  );
}
